import { Show, createSignal, createEffect, untrack, For } from "solid-js"
import type { MarketplaceSkill, CreateCategoryInput, UpdateSkillAttributes } from "./skill-market-types"

// 技能属性编辑弹框（metadata-only，不动文件）
// 字段与上传表单一致，但去掉「文件选择」与「技能名称」（name 不可改）。
// 成功回调 onSubmit(metadata) → 由 controller 调 PATCH /:skillId/attributes。
export function SkillMarketAttributes(props: {
  show: boolean
  saving: boolean
  onClose: () => void
  onSubmit: (metadata: UpdateSkillAttributes) => void
  prefillSkill?: MarketplaceSkill | null
  // 分类下拉选项（来自后端 GET /api/skill-market/categories，已剔除 "all"）；为空时回退硬编码 6 项
  categories?: { code: string; label: string }[]
  // 创建自定义分类（写入 skill_category 表）；成功后返回新分类，用于自动选中
  onCreateCategory?: (input: CreateCategoryInput) => Promise<{ code: string } | null>
  creatingCategory?: boolean
}) {
  const [displayName, setDisplayName] = createSignal("")
  const [description, setDescription] = createSignal("")
  const [category, setCategory] = createSignal("other")
  const [version, setVersion] = createSignal("1.0.0")
  const [author, setAuthor] = createSignal("")
  const [tags, setTags] = createSignal("")

  // 「新加分类」子弹框本地状态
  const [showAddCategory, setShowAddCategory] = createSignal(false)
  const [newCode, setNewCode] = createSignal("")
  const [newLabel, setNewLabel] = createSignal("")
  const [newSortOrder, setNewSortOrder] = createSignal("")

  // 弹框打开时用原 skill 的已保存值回填（与 upload 组件一致的 prefill 模式）
  createEffect(() => {
    if (!props.show) return
    const skill = props.prefillSkill
    if (!skill) return
    // untrack 避免单个 signal 变化时重复触发
    untrack(() => {
      setDisplayName(skill.display_name ?? "")
      setDescription(skill.description ?? "")
      setCategory(skill.category ?? "other")
      setVersion(skill.version ?? "1.0.0")
      setAuthor(skill.author ?? "")
      setTags(skill.tags ? skill.tags.join(", ") : "")
    })
  })

  function handleSubmit() {
    const metadata: UpdateSkillAttributes = {
      display_name: displayName() || undefined,
      description: description() || undefined,
      category: category(),
      version: version() || undefined,
      author: author() || undefined,
      tags: tags() ? tags().split(",").map((t) => t.trim()).filter(Boolean) : undefined,
    }
    props.onSubmit(metadata)
  }

  // 「新加分类」作为 select 内固定 option（类似「全部技能」），仅作点击触发入口：
  // 选中时立即把 select 回退到当前分类（命令式 el.value=category()，避免被选中/回填），再开弹框。
  const NEW_CATEGORY_VALUE = "__new_category__"
  function openAddCategory() {
    setNewCode("")
    setNewLabel("")
    setNewSortOrder("")
    setShowAddCategory(true)
  }

  function handleCategoryChange(e: Event) {
    const el = e.currentTarget as HTMLSelectElement
    if (el.value === NEW_CATEGORY_VALUE) {
      el.value = category() // 命令式回退，不让「新加分类」成为选中值/回填
      openAddCategory()
      return
    }
    setCategory(el.value)
  }

  // 确认新加分类 →  onCreateCategory；成功后选中新分类并关闭子弹框
  async function handleCreateCategory() {
    const code = newCode().trim()
    const label = newLabel().trim()
    if (!code || !label) return
    const sortNum = newSortOrder().trim() === "" ? undefined : Number(newSortOrder())
    const created = await props.onCreateCategory?.({
      code,
      label,
      sort_order: Number.isNaN(sortNum) ? 0 : (sortNum ?? 0),
    })
    if (created) {
      setCategory(created.code)
      setShowAddCategory(false)
    }
  }

  return (
    <Show when={props.show}>
      <div class="skill-market-upload-overlay" onClick={props.onClose}>
        <div class="skill-market-upload-dialog" onClick={(e) => e.stopPropagation()}>
          <div class="skill-market-upload-header">
            <span class="skill-market-upload-title">技能属性</span>
            <button class="skill-market-upload-close" onClick={props.onClose}>✕</button>
          </div>

          <div style={{ display: "flex", "flex-direction": "column", gap: "16px" }}>
            <div class="skill-market-upload-field">
              <span class="skill-market-upload-label">显示名称</span>
              <input
                type="text"
                value={displayName()}
                onInput={(e) => setDisplayName(e.currentTarget.value)}
                placeholder="我的技能"
                class="skill-market-upload-input"
              />
            </div>

            <div class="skill-market-upload-field">
              <span class="skill-market-upload-label">描述</span>
              <textarea
                value={description()}
                onInput={(e) => setDescription(e.currentTarget.value)}
                placeholder="技能描述..."
                rows={2}
                class="skill-market-upload-input"
                style={{ resize: "vertical" }}
              />
            </div>

            <div style={{ display: "grid", "grid-template-columns": "1fr 1fr 1fr", gap: "12px" }}>
              <div class="skill-market-upload-field">
                <span class="skill-market-upload-label">分类</span>
                <select
                  value={category()}
                  onChange={handleCategoryChange}
                  class="skill-market-upload-input"
                >
                  <Show
                    when={props.categories && props.categories.length > 0}
                    fallback={
                      <>
                        <option value="security-audit">安全审计</option>
                        <option value="vulnerability-scan">漏洞扫描</option>
                        <option value="compliance">合规检查</option>
                        <option value="incident-response">应急响应</option>
                        <option value="forensics">数字取证</option>
                        <option value="other">其他</option>
                        <option value={NEW_CATEGORY_VALUE}>--新加分类--</option>
                      </>
                    }
                  >
                    <For each={props.categories}>
                      {(cat) => <option value={cat.code}>{cat.label}</option>}
                    </For>
                    <option value={NEW_CATEGORY_VALUE}>--新加分类--</option>
                  </Show>
                </select>
              </div>
              <div class="skill-market-upload-field">
                <span class="skill-market-upload-label">版本</span>
                <input
                  type="text"
                  value={version()}
                  onInput={(e) => setVersion(e.currentTarget.value)}
                  placeholder="1.0.0"
                  class="skill-market-upload-input"
                />
              </div>
              <div class="skill-market-upload-field">
                <span class="skill-market-upload-label">作者</span>
                <input
                  type="text"
                  value={author()}
                  onInput={(e) => setAuthor(e.currentTarget.value)}
                  placeholder="作者"
                  class="skill-market-upload-input"
                />
              </div>
            </div>

            <div class="skill-market-upload-field">
              <span class="skill-market-upload-label">标签（逗号分隔）</span>
              <input
                type="text"
                value={tags()}
                onInput={(e) => setTags(e.currentTarget.value)}
                placeholder="tag1, tag2, tag3"
                class="skill-market-upload-input"
              />
            </div>

            <div class="skill-market-upload-actions">
              <button class="skill-market-detail-btn-ghost" onClick={props.onClose}>
                取消
              </button>
              <button
                class="skill-market-detail-btn-primary"
                disabled={props.saving}
                onClick={handleSubmit}
              >
                {props.saving ? "保存中..." : "确认"}
              </button>
            </div>
          </div>
        </div>

        {/* 新加分类子弹框（叠加在属性弹框之上，复用同一套 CSS） */}
        <Show when={showAddCategory()}>
          <div class="skill-market-upload-overlay" onClick={() => setShowAddCategory(false)}>
            <div class="skill-market-upload-dialog" onClick={(e) => e.stopPropagation()}>
              <div class="skill-market-upload-header">
                <span class="skill-market-upload-title">新加分类</span>
                <button class="skill-market-upload-close" onClick={() => setShowAddCategory(false)}>✕</button>
              </div>

              <div style={{ display: "flex", "flex-direction": "column", gap: "16px" }}>
                <div class="skill-market-upload-field">
                  <span class="skill-market-upload-label">类型编码 *</span>
                  <input
                    type="text"
                    value={newCode()}
                    onInput={(e) => setNewCode(e.currentTarget.value)}
                    placeholder="custom-category"
                    class="skill-market-upload-input"
                  />
                </div>
                <div class="skill-market-upload-field">
                  <span class="skill-market-upload-label">类型名称 *</span>
                  <input
                    type="text"
                    value={newLabel()}
                    onInput={(e) => setNewLabel(e.currentTarget.value)}
                    placeholder="自定义分类"
                    class="skill-market-upload-input"
                  />
                </div>
                <div class="skill-market-upload-field">
                  <span class="skill-market-upload-label">排序</span>
                  <input
                    type="number"
                    value={newSortOrder()}
                    onInput={(e) => setNewSortOrder(e.currentTarget.value)}
                    placeholder="0"
                    class="skill-market-upload-input"
                  />
                </div>

                <div class="skill-market-upload-actions">
                  <button class="skill-market-detail-btn-ghost" onClick={() => setShowAddCategory(false)}>
                    取消
                  </button>
                  <button
                    class="skill-market-detail-btn-primary"
                    disabled={props.creatingCategory || !newCode().trim() || !newLabel().trim()}
                    onClick={handleCreateCategory}
                  >
                    {props.creatingCategory ? "保存中..." : "确认"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Show>
      </div>
    </Show>
  )
}
